import { ReactNode, useRef, useEffect } from 'react';
import useDropdownPosition from '../../hooks/useDropdownPosition';

interface DropdownMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  position?: 'left' | 'right';
  width?: 'auto' | 'sm' | 'md' | 'lg' | 'xl';
}

const widthStyles = {
  auto: '',
  sm: 'w-32',
  md: 'min-w-[160px]',
  lg: 'w-64',
  xl: 'min-w-[320px]',
};

export default function DropdownMenu({
  isOpen,
  onClose,
  children,
  className = '',
  position = 'right',
  width = 'md',
}: DropdownMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  useDropdownPosition(menuRef, isOpen, position);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const positionStyles = position === 'left' ? 'left-0' : 'right-0';

  return (
    <div
      ref={menuRef}
      className={`absolute top-full mt-1 ${positionStyles} z-20 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 ${widthStyles[width]} ${className}`}
    >
      {children}
    </div>
  );
}

interface DropdownMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  className?: string;
}

export function DropdownMenuItem({
  children,
  onClick,
  variant = 'default',
  disabled = false,
  className = '',
}: DropdownMenuItemProps) {
  const variantStyles =
    variant === 'danger'
      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full px-4 py-2 text-left text-sm ${variantStyles} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}
